import React, { useState, useEffect, useRef, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './ProcessPage.module.css';
import { useOutletContext, Link } from 'react-router-dom';
import {
    UploadCloud, FileText, X, XCircle, CheckCircle2, AlertTriangle,
    Pencil, RefreshCw, Download, Mail, Clock,
    ChevronLeft, ChevronRight,
    UserPlus, Sparkles, Gauge, Languages, Plug,
    Info, // ← ajouté
} from 'lucide-react';

import PptxEditor from './PptxEditor';
// --- IMPORT DU NOUVEAU COMPOSANT ---
import ManualUploadModal from '../components/ManualUploadModal';

const ProcessPage = () => {
    const { refreshToken, contactEmail, phone, quota } = useAuth();
    const { setIsNavbarVisible } = useOutletContext() || { setIsNavbarVisible: () => {} };

    // --- ÉTATS UPLOAD (BATCH) ---
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);

    // --- ÉTAT VISIBILITÉ MODAL MANUEL ---
    const [showManualModal, setShowManualModal] = useState(false);

    // --- ÉTATS HISTORIQUE / DASHBOARD & PAGINATION ---
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    // NOUVEAU : État pour la pagination
    const [pagination, setPagination] = useState({
        page: 1,
        totalPages: 1,
        totalItems: 0
    });

    // --- REF POUR LE POLLING ---
    const pollingTimeoutRef = useRef(null);

    // --- ÉTATS ÉDITEUR ---
    const [showEditor, setShowEditor] = useState(false);
    const [editorData, setEditorData] = useState({
        fileUrl: null,
        fileName: '',
        pptxBlobName: ''
    });

    // --- CONFIG API (Boond/Hubspot) ---
    const [apiConfig, setApiConfig] = useState({
        boond: false,
        hubspot: false,
        myhub: false,
        loading: true
    });

    // --- ÉTAT FICHE DE POSTE (optionnelle) ---
    const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
    const jobDescInputRef = useRef(null);

    // 1. CHARGEMENT CONFIG API
    useEffect(() => {
        const checkApiConfiguration = async () => {
            try {
                const response = await api.get('/config/boondmanager');
                const data = response.data;
                setApiConfig({
                    boond: !!(data.user_token && data.client_token),
                    hubspot: !!(data.hubspot_dev_key),
                    myhub: !!(data.myhub_api_key),
                    loading: false
                });
            } catch (error) {
                console.error("Erreur config API", error);
                setApiConfig(prev => ({ ...prev, loading: false }));
            }
        };
        checkApiConfiguration();
    }, []);

    // 2. LOGIQUE DE POLLING INTELLIGENTE ET PAGINÉE
    const fetchHistory = useCallback(async (pageToFetch = null) => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        const targetPage = pageToFetch || pagination.page;

        try {
            const res = await api.get(`/history/generations?page=${targetPage}&per_page=10`);
            const data = res.data;

            if (Array.isArray(data)) {
                setHistory(data);
                setPagination({ page: 1, totalPages: 1, totalItems: data.length });
            } else {
                setHistory(data.items || []);
                setPagination({
                    page: data.page,
                    totalPages: data.total_pages,
                    totalItems: data.total
                });
            }

            setLoadingHistory(false);

            const currentItems = Array.isArray(data) ? data : data.items;
            const hasActiveTasks = currentItems?.some(item =>
                item.status === 'Pending' || item.status === 'Processing'
            );

            if (hasActiveTasks) {
                pollingTimeoutRef.current = setTimeout(() => fetchHistory(targetPage), 5000);
            }

        } catch (err) {
            console.error("Erreur chargement historique", err);
            pollingTimeoutRef.current = setTimeout(() => fetchHistory(targetPage), 15000);
        }
    }, [pagination.page]);

    // Lancer le polling au montage
    useEffect(() => {
        fetchHistory(1);
        return () => {
            if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
        };
    }, []);

    // 3. GESTION NAVBAR
    useEffect(() => {
        if (showEditor) {
            setIsNavbarVisible(false);
        } else {
            setIsNavbarVisible(true);
        }
        return () => setIsNavbarVisible(true);
    }, [showEditor, setIsNavbarVisible]);


    // --- GESTION DES FICHIERS (DRAG & DROP) ---
    const handleFileChange = (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const newFiles = Array.from(fileList).filter(file =>
            file.type === "application/pdf" ||
            file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        );
        if (newFiles.length < fileList.length) setUploadError("Certains fichiers ont été ignorés (format invalide).");
        else setUploadError('');
        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const handleDragEvents = (e, over) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(over);
    };

    const handleDrop = (e) => {
        handleDragEvents(e, false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files);
        }
    };

    const removeFile = (indexToRemove) => {
        setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
        if (selectedFiles.length <= 1) {
            if (fileInputRef.current) fileInputRef.current.value = null;
            setUploadError('');
        }
    };

    // --- SOUMISSION STANDARD (BATCH) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedFiles.length === 0) {
            setUploadError('Veuillez sélectionner au moins un fichier.');
            return;
        }
        if (quota !== null && typeof quota !== 'undefined' && quota < selectedFiles.length) {
            setUploadError(`Crédits insuffisants (${quota}) pour ${selectedFiles.length} fichier(s).`);
            return;
        }
        setUploading(true);
        setUploadError('');

        const formData = new FormData();
        selectedFiles.forEach(file => formData.append('cv_files', file));
        formData.append('language', e.target.language.value);
        formData.append('import_boondmanager', e.target.boondManagerImport.checked);
        formData.append('import_hubspot', e.target.hubSpotImport.checked);
        formData.append('import_myhubconsulting', e.target.myHubImport.checked);
        if (contactEmail) formData.append('user_email', contactEmail);
        if (phone) formData.append('user_phone', phone);
        if (jobDescriptionFile) {
            formData.append('job_description_file', jobDescriptionFile);
        }

        try {
            const response = await api.post('/process', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.new_token) refreshToken(response.data.new_token);
            setSelectedFiles([]);
            setJobDescriptionFile(null);          // ← ajout
            if (jobDescInputRef.current) jobDescInputRef.current.value = null; // ← ajout
            if (fileInputRef.current) fileInputRef.current.value = null;
            setLoadingHistory(true);
            fetchHistory(1);
        } catch (error) {
            console.error(error);
            setUploadError(error.response?.data?.error || "Erreur lors de l'envoi des fichiers.");
        } finally {
            setUploading(false);
        }
    };

    // --- GESTION CHANGEMENT DE PAGE ---
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setLoadingHistory(true);
            fetchHistory(newPage);
        }
    };

    // --- ACTIONS DASHBOARD ---
    const handleDownload = async (blobName, originalName) => {
        if (!blobName) return;
        try {
            const response = await api.get(`/download/${blobName}`, { responseType: 'blob' });
            let filename = 'Dossier.pptx';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="([^"]+)"/);
                if (match && match[1]) filename = match[1];
            } else if (originalName) {
                filename = `Dossier_${originalName.split('.')[0]}.pptx`;
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur download:', error);
            alert('Erreur lors du téléchargement.');
        }
    };

    const sendFileByEmail = async (blobName) => {
        if (!blobName) return;
        if (!window.confirm("Envoyer ce dossier par email ?")) return;
        try {
            const response = await api.post('/pptx/email', { blob_name: blobName });
            alert(response.data.message || "Email envoyé !");
        } catch (err) {
            console.error(err);
            alert("Erreur lors de l'envoi.");
        }
    };

    const handleEdit = (item) => {
        setEditorData({
            fileUrl: null,
            fileName: item.filename,
            pptxBlobName: item.blob_name
        });
        setShowEditor(true);
    };

    const handleManualRefresh = () => {
        setLoadingHistory(true);
        fetchHistory(pagination.page);
    };

    const handleJobDescriptionChange = (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const file = fileList[0];
        const validTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "text/plain"
        ];
        if (!validTypes.includes(file.type)) {
            setUploadError("La fiche de poste doit être un PDF, Word (.docx) ou .txt.");
            return;
        }
        setJobDescriptionFile(file);
    };

    const removeJobDescription = () => {
        setJobDescriptionFile(null);
        if (jobDescInputRef.current) jobDescInputRef.current.value = null;
    };

    const renderConfigStatus = (isConfigured) => {
        if (apiConfig.loading) return <span className={styles.configLoading}>Vérification…</span>;
        if (isConfigured) {
            return (
                <span className={`${styles.configStatus} ${styles.configStatusSuccess}`} title="Configuré">
                    <CheckCircle2 size={13} strokeWidth={2} /> Configuré
                </span>
            );
        }
        return (
            <span className={`${styles.configStatus} ${styles.configStatusError}`} title="Non configuré">
                <XCircle size={13} strokeWidth={2} />
                <Link to="/settings/boondManager" className={styles.configLink}>Configurer</Link>
            </span>
        );
    };

    return (
        <div className={styles.pageContainer}>

            {/* 1. ZONE UPLOAD */}
            {!showEditor && (
                <section className={styles.uploadSection}>

                    <div className={styles.sectionHeader}>
                        <div className={styles.headerText}>
                            <span className={styles.eyebrowBadge}>
                                <Sparkles size={12} strokeWidth={2.5} />
                                Génération IA
                            </span>
                            <h2 className={styles.pageTitle}>Générateur de Dossiers</h2>
                            <p className={styles.pageSubtitle}>Glissez un ou plusieurs CVs pour lancer la génération.</p>
                            <p className={styles.infoNote} title="Au-delà de 18 missions, seules les 18 plus pertinentes/récentes seront affichées.">
                                <Info size={13} strokeWidth={2} />
                                18 missions maximum affichées par dossier généré
                            </p>
                        </div>

                        <button
                            type="button"
                            className={styles.manualBtn}
                            onClick={() => setShowManualModal(true)}
                            title="Ajouter un candidat sans analyse IA (Gratuit)"
                        >
                            <UserPlus size={16} strokeWidth={2} />
                            Dépôt Manuel (CRM)
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className={styles.form}>
                        {quota !== null && typeof quota !== 'undefined' && (
                            <div className={styles.quotaBadge}>
                                <Gauge size={14} strokeWidth={2.5} />
                                Crédits disponibles
                                <strong className={styles.quotaValue}>{quota}</strong>
                            </div>
                        )}

                        <div
                            className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''}`}
                            onDragOver={(e) => handleDragEvents(e, true)}
                            onDragLeave={(e) => handleDragEvents(e, false)}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('cvFiles').click()}
                        >
                            <input
                                type="file" id="cvFiles" multiple hidden
                                onChange={(e) => handleFileChange(e.target.files)}
                                accept=".pdf,.docx,.pptx" ref={fileInputRef}
                            />

                            {selectedFiles.length > 0 ? (
                                <div className={styles.filesListContainer}>
                                    <p className={styles.filesListHeading}>
                                        <strong>{selectedFiles.length} fichier(s) sélectionné(s)</strong>
                                    </p>
                                    <ul className={styles.selectedFilesList}>
                                        {selectedFiles.map((f, index) => (
                                            <li key={index} className={styles.fileItem}>
                                                <span className={styles.fileItemInfo}>
                                                    <FileText size={15} strokeWidth={2} className={styles.fileItemIcon} />
                                                    {f.name}
                                                </span>
                                                <X
                                                    size={16}
                                                    strokeWidth={2}
                                                    className={styles.removeIcon}
                                                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className={styles.dropZonePrompt}>
                                    <span className={styles.dropZoneIconWrap}>
                                        <UploadCloud size={28} strokeWidth={1.75} />
                                    </span>
                                    <p className={styles.dropZoneTitle}>Glissez vos fichiers ici</p>
                                    <span className={styles.browseLink}>ou cliquez pour parcourir</span>
                                    <span className={styles.dropZoneHint}>PDF, Word, PowerPoint</span>
                                </div>
                            )}
                        </div>

                        {uploadError && (
                            <div className={styles.alertError} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AlertTriangle size={15} strokeWidth={2} />
                                    <span>{uploadError}</span>
                                </div>
                                <Link to="/support" style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'inherit', textDecoration: 'underline', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                    <Mail size={14} strokeWidth={2} /> Créer un ticket
                                </Link>
                            </div>
                        )}

                        <div className={styles.jobDescSection}>
                            <label className={styles.formLabel}>
                                <FileText size={14} strokeWidth={2} />
                                Fiche de poste (optionnel)
                            </label>
                            <p className={styles.jobDescHint}>
                                Si fournie, les 3 missions les plus pertinentes du CV seront choisies en fonction de cette fiche de poste.
                            </p>

                            {jobDescriptionFile ? (
                                <div className={styles.fileItem}>
                                    <span className={styles.fileItemInfo}>
                                        <FileText size={15} strokeWidth={2} className={styles.fileItemIcon} />
                                        {jobDescriptionFile.name}
                                    </span>
                                    <X
                                        size={16}
                                        strokeWidth={2}
                                        className={styles.removeIcon}
                                        onClick={removeJobDescription}
                                    />
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.jobDescUploadBtn}
                                    onClick={() => jobDescInputRef.current?.click()}
                                >
                                    <UploadCloud size={15} strokeWidth={2} />
                                    Ajouter une fiche de poste
                                </button>
                            )}

                            <input
                                type="file"
                                hidden
                                ref={jobDescInputRef}
                                accept=".pdf,.docx,.txt"
                                onChange={(e) => handleJobDescriptionChange(e.target.files)}
                            />
                        </div>

                        <div className={styles.formOptions}>
                            <div className={styles.formGroup}>
                                <label htmlFor="language" className={styles.formLabel}>
                                    <Languages size={14} strokeWidth={2} />
                                    Langue
                                </label>
                                <select name="language" id="language" className={styles.formSelect}>
                                    <option value="FR">Français</option>
                                    <option value="ENG">English (Prototype)</option>
                                </select>
                            </div>

                            <div className={styles.integrationsGroup}>
                                <p className={styles.formLabel}>
                                    <Plug size={14} strokeWidth={2} />
                                    Connecteurs CRM
                                </p>

                                <div className={styles.integrationsList}>

                                    <div className={styles.integrationRow}>
                                        <div className={styles.integrationInfo}>
                                            <span className={styles.integrationAvatar}>BM</span>
                                            <span className={styles.integrationName}>BoondManager</span>
                                        </div>
                                        <div className={styles.integrationControls}>
                                            {renderConfigStatus(apiConfig.boond)}
                                            <label className={`${styles.toggleWrap} ${!apiConfig.boond ? styles.toggleDisabled : ''}`}>
                                                <input
                                                    type="checkbox" name="boondManagerImport"
                                                    defaultChecked={apiConfig.boond}
                                                    disabled={!apiConfig.boond}
                                                    className={styles.toggleInput}
                                                />
                                                <span className={styles.toggleTrack}></span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className={styles.integrationRow}>
                                        <div className={styles.integrationInfo}>
                                            <span className={styles.integrationAvatar}>HS</span>
                                            <span className={styles.integrationName}>HubSpot</span>
                                        </div>
                                        <div className={styles.integrationControls}>
                                            {renderConfigStatus(apiConfig.hubspot)}
                                            <label className={`${styles.toggleWrap} ${!apiConfig.hubspot ? styles.toggleDisabled : ''}`}>
                                                <input
                                                    type="checkbox" name="hubSpotImport"
                                                    defaultChecked={apiConfig.hubspot}
                                                    disabled={!apiConfig.hubspot}
                                                    className={styles.toggleInput}
                                                />
                                                <span className={styles.toggleTrack}></span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className={styles.integrationRow}>
                                        <div className={styles.integrationInfo}>
                                            <span className={styles.integrationAvatar}>MH</span>
                                            <span className={styles.integrationName}>MyHubConsulting</span>
                                        </div>
                                        <div className={styles.integrationControls}>
                                            {renderConfigStatus(apiConfig.myhub)}
                                            <label className={`${styles.toggleWrap} ${!apiConfig.myhub ? styles.toggleDisabled : ''}`}>
                                                <input
                                                    type="checkbox" name="myHubImport"
                                                    defaultChecked={apiConfig.myhub}
                                                    disabled={!apiConfig.myhub}
                                                    className={styles.toggleInput}
                                                />
                                                <span className={styles.toggleTrack}></span>
                                            </label>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <button type="submit" className={styles.submitBtn} disabled={uploading || selectedFiles.length === 0}>
                                {uploading ? (
                                    <>
                                        <RefreshCw size={16} strokeWidth={2} className={styles.spinIcon} />
                                        Envoi en cours...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={16} strokeWidth={2} />
                                        Lancer {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </section>
            )}

            {/* 2. TABLEAU DE BORD */}
            {!showEditor && (
                <section className={styles.historySection}>
                    <div className={styles.historyHeader}>
                        <h3 className={styles.historyTitle}>Historique &amp; Statut</h3>
                        <button onClick={handleManualRefresh} className={styles.refreshBtn}>
                            <RefreshCw size={14} strokeWidth={2} /> Actualiser
                        </button>
                    </div>

                    {loadingHistory ? (
                        <div className={styles.loaderWrap}>
                            <span className={styles.loader}></span>
                        </div>
                    ) : (
                        <>
                            <div className={styles.tableResponsive}>
                                <table className={styles.historyTable}>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Fichier</th>
                                            <th>Statut</th>
                                            <th className={styles.actionsHeader}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((item) => (
                                            <tr key={item.id}>
                                                <td className={styles.dateCell}>
                                                    {item.created_at ? (() => {
                                                        let date = new Date(item.created_at);
                                                        date.setHours(date.getHours() + 1);
                                                        return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                                    })() : '-'}
                                                </td>
                                                <td title={item.filename} className={styles.filenameCell}>
                                                    {item.filename && item.filename.length > 30 ? item.filename.substring(0, 28) + '...' : item.filename}
                                                </td>
                                                <td>
                                                    {item.status === 'Pending' && (
                                                        <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                                                            <Clock size={13} strokeWidth={2} /> En attente
                                                        </span>
                                                    )}
                                                    {item.status === 'Processing' && (
                                                        <span className={`${styles.statusBadge} ${styles.statusProcessing}`}>
                                                            <RefreshCw size={13} strokeWidth={2} className={styles.spinIcon} /> En cours
                                                        </span>
                                                    )}
                                                    {item.status === 'Completed' && (
                                                        <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>
                                                            <CheckCircle2 size={13} strokeWidth={2} /> Prêt
                                                        </span>
                                                    )}
                                                    {item.status === 'Error' && (
                                                        <span className={`${styles.statusBadge} ${styles.statusError}`} title={item.error}>
                                                            <AlertTriangle size={13} strokeWidth={2} /> Erreur
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {/* CAS 1 : C'est un vrai fichier PPTX (Status Completed ET ce n'est pas le placeholder manuel) */}
                                                    {item.status === 'Completed' && item.blob_name !== 'Manually_Processed_No_PPTX' && (
                                                        <div className={styles.actionButtons}>
                                                            <button onClick={() => handleDownload(item.blob_name, item.filename)} className={styles.iconBtn} title="Télécharger">
                                                                <Download size={15} strokeWidth={2} />
                                                            </button>
                                                            <button onClick={() => sendFileByEmail(item.blob_name)} className={styles.iconBtn} title="Email">
                                                                <Mail size={15} strokeWidth={2} />
                                                            </button>
                                                            <button onClick={() => handleEdit(item)} className={styles.iconBtn} title="Éditer">
                                                                <Pencil size={15} strokeWidth={2} />
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* CAS 2 : C'est un import manuel (On affiche juste un texte informatif) */}
                                                    {item.status === 'Completed' && item.blob_name === 'Manually_Processed_No_PPTX' && (
                                                        <span className={styles.crmSyncedTag}>
                                                            <CheckCircle2 size={13} strokeWidth={2} /> CRM Synced
                                                        </span>
                                                    )}

                                                    {/* CAS 3 : La tâche est en erreur */}
                                                    {item.status === 'Error' && (
                                                        <div className={styles.actionButtons}>
                                                            <Link to="/support" className={styles.iconBtn} title="Signaler cette erreur au support">
                                                                <Mail size={15} strokeWidth={2} />
                                                            </Link>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {history.length === 0 && (
                                            <tr><td colSpan="4" className={styles.emptyState}>Aucun historique récent. Lancez une génération !</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {history.length > 0 && (
                                <div className={styles.paginationControls}>
                                    <button
                                        className={styles.paginationBtn}
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={pagination.page === 1}
                                    >
                                        <ChevronLeft size={15} strokeWidth={2} /> Précédent
                                    </button>
                                    <span className={styles.paginationInfo}>Page {pagination.page} sur {pagination.totalPages || 1}</span>
                                    <button
                                        className={styles.paginationBtn}
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={pagination.page >= pagination.totalPages}
                                    >
                                        Suivant <ChevronRight size={15} strokeWidth={2} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>
            )}

            {/* 3. MODAL ÉDITEUR */}
            {showEditor && (
                <PptxEditor fileUrl={editorData.fileUrl} fileName={editorData.fileName} pptxBlobName={editorData.pptxBlobName} onClose={() => setShowEditor(false)} />
            )}

            {/* 4. MODAL AJOUT MANUEL */}
            {showManualModal && (
                <ManualUploadModal
                    onClose={() => setShowManualModal(false)}
                    onSuccess={() => { setLoadingHistory(true); fetchHistory(1); }}
                    userEmail={contactEmail}
                    apiConfig={apiConfig}
                />
            )}

        </div>
    );
};

export default ProcessPage;